import {
  Alert, Box, Breadcrumbs, Button, Chip, Divider, IconButton, Link, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import type { CellTrace } from './types';

interface Breadcrumb { column: string; rowIndex: number }

interface Props {
  rowIndex: number;
  column: string;
  trace: CellTrace;
  onClose: () => void;
  /** Inline actions for the calculation that produced this column. When
   *  these are omitted the panel renders in read-only mode. */
  canEdit?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onEditCalc?: () => void;
  onDeleteCalc?: () => void;
  onMoveCalc?: (delta: number) => void;
  /** Drill-down support. */
  breadcrumbs?: Breadcrumb[];
  onJumpDepth?: (depth: number) => void;
  onBack?: () => void;
  /** Given an input name on the current trace, return the trace for the calc
   *  that produced it (same row) -- or null if the input is a raw column. */
  resolveDrillTrace?: (inputName: string) => CellTrace | null;
  onDrillInto?: (inputName: string, trace: CellTrace) => void;
  humanize?: (s: string) => string;
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Numeric-aware comparator: compares as numbers when both sides parse, else
 *  falls back to a locale string compare. Null/undefined sort last. */
function compareKeys(a: unknown, b: unknown): number {
  const aNil = a === null || a === undefined;
  const bNil = b === null || b === undefined;
  if (aNil || bNil) return aNil === bNil ? 0 : aNil ? 1 : -1;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b));
}

export default function TracePanel({
  rowIndex, column, trace, onClose,
  canEdit, canMoveUp, canMoveDown,
  onEditCalc, onDeleteCalc, onMoveCalc,
  breadcrumbs, onJumpDepth, onBack,
  resolveDrillTrace, onDrillInto, humanize,
}: Props) {
  const label = (s: string) => (humanize ? humanize(s) : s);
  const crumbs = breadcrumbs && breadcrumbs.length > 0
    ? breadcrumbs
    : [{ column, rowIndex }];
  return (
    <Paper
      elevation={4}
      sx={{
        position: 'fixed', right: 16, top: 96, bottom: 16,
        width: { xs: '90vw', sm: 520 }, maxWidth: '90vw',
        display: 'flex', flexDirection: 'column',
        zIndex: 1200,
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ p: 1.5, pb: 0.5 }}>
        {onBack && (
          <Tooltip title="Back to previous trace">
            <IconButton size="small" onClick={onBack} aria-label="back" sx={{ mr: 0.5 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            How this figure was worked out · row {rowIndex}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label(column)}
            </Typography>
            <Chip size="small" label={`= ${fmt(trace.result)}`} color="primary" />
          </Stack>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Stack>

      {crumbs.length > 1 && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Breadcrumbs separator="›" aria-label="drill path" sx={{ fontSize: 13 }}>
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1;
              if (isLast) {
                return (
                  <Typography key={i} variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                    {label(c.column)}
                  </Typography>
                );
              }
              return (
                <Link
                  key={i}
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => onJumpDepth?.(i)}
                >
                  {label(c.column)}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>
      )}

      {canEdit && (
        <Stack direction="row" spacing={1} sx={{ px: 1.5, pb: 1 }}>
          <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={onEditCalc}>
            Edit calculation
          </Button>
          <Tooltip title="Run this calculation earlier"><span>
            <IconButton size="small" disabled={!canMoveUp} onClick={() => onMoveCalc?.(-1)}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Run this calculation later"><span>
            <IconButton size="small" disabled={!canMoveDown} onClick={() => onMoveCalc?.(+1)}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span></Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onDeleteCalc}
          >
            Delete column
          </Button>
        </Stack>
      )}
      <Divider />

      <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 1.5 }}>
        {trace.error && <Alert severity="warning" sx={{ mb: 1 }}>{trace.error}</Alert>}

        {trace.calc_type === 'R' ? (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Source column</Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
              <Typography variant="body2">
                <strong>{label(column)}</strong> was fetched directly by the source SQL —
                no calculation produced it, so there is no further drill-down.
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                <Chip size="small" label={`value = ${fmt(trace.result)}`} />
              </Stack>
            </Paper>
          </>
        ) : (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Expression</Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
              <code style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{trace.expression || '—'}</code>
            </Paper>
          </>
        )}

        {trace.rendered && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>With this row's values</Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
              <code style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{trace.rendered}</code>
            </Paper>
          </>
        )}

        {trace.inputs && Object.keys(trace.inputs).length > 0 && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Inputs used</Typography>
              {resolveDrillTrace && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  · click a highlighted input to see how it was worked out
                </Typography>
              )}
            </Stack>
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {Object.entries(trace.inputs).map(([k, v]) => {
                  const drillable = resolveDrillTrace?.(k) ?? null;
                  const chipLabel = `${label(k)} = ${fmt(v)}`;
                  if (drillable && onDrillInto) {
                    return (
                      <Tooltip key={k} title={`Drill into ${label(k)}`}>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          clickable
                          icon={<SearchIcon fontSize="small" />}
                          label={chipLabel}
                          onClick={() => onDrillInto(k, drillable)}
                          sx={{ mb: 0.5 }}
                        />
                      </Tooltip>
                    );
                  }
                  return (
                    <Chip
                      key={k}
                      size="small"
                      variant="outlined"
                      label={chipLabel}
                      sx={{ mb: 0.5 }}
                    />
                  );
                })}
              </Stack>
            </Paper>
          </>
        )}

        {trace.calc_type === 'C' && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Decision</Typography>
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {trace.condition} → <strong>{String(trace.condition_result)}</strong>
                {' '}⇒ {trace.branch_taken === 'true' ? 'Then' : 'Else'} branch
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Branch expression: <code>{trace.branch_expression}</code>
              </Typography>
            </Paper>
          </Box>
        )}

        {trace.calc_type === 'L' && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Lookup</Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={0.5}>
                <Chip size="small" label={`Table: ${trace.lookup_table || '∅'}`} />
                {trace.lookup_type && <Chip size="small" label={`Type: ${trace.lookup_type}`} />}
                {trace.key_data_type && <Chip size="small" label={`Key dtype: ${trace.key_data_type}`} />}
                {trace.result_data_type && <Chip size="small" label={`Result dtype: ${trace.result_data_type}`} />}
              </Stack>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                key <strong>{trace.key_column}</strong> = {fmt(trace.key_value)}
                {trace.date_column ? <>, date <strong>{trace.date_column}</strong> = {fmt(trace.date_value)}</> : null}
              </Typography>
              {trace.why && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                  {trace.matched_index !== undefined
                    ? <CheckCircleIcon fontSize="small" color="success" />
                    : <HighlightOffIcon fontSize="small" color="warning" />}
                  <Typography variant="caption">{trace.why}</Typography>
                </Stack>
              )}
            </Paper>

            {trace.candidates && trace.candidates.length > 0 && (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Slab / entry candidates ({trace.candidates.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>Min</TableCell>
                        <TableCell>Max</TableCell>
                        <TableCell>Result</TableCell>
                        <TableCell>Why</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...trace.candidates]
                        .sort((a, b) => compareKeys(a.min_key_value, b.min_key_value))
                        .map((c, i) => (
                        <TableRow
                          key={i}
                          sx={{ bgcolor: c.matched ? 'success.lighter' : undefined, fontFamily: 'monospace' }}
                        >
                          <TableCell sx={{ width: 28 }}>
                            {c.matched
                              ? <CheckCircleIcon fontSize="small" color="success" />
                              : <HighlightOffIcon fontSize="small" sx={{ color: 'grey.400' }} />}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{fmt(c.min_key_value)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{fmt(c.max_key_value)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{fmt(c.result_value)}</TableCell>
                          <TableCell sx={{ fontSize: 13 }}>{c.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}
